import { useEffect, useMemo, useState } from "react";
import { MessagesSquare, Sparkles, BookOpen, CalendarDays, Bot, User } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { AdminHeader, StatCard } from "@/components/admin/AdminShared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ChatLogsAdmin() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/chat-logs").then((r) => setData(r.data)); }, []);

  const sessions = useMemo(() => {
    if (!data) return [];
    const map = {};
    for (const l of data.logs) {
      if (!map[l.session_id]) map[l.session_id] = [];
      map[l.session_id].push(l);
    }
    return Object.entries(map)
      .map(([sid, msgs]) => ({
        sid,
        msgs,
        first: msgs[0]?.created_at || "",
        last: msgs[msgs.length - 1]?.created_at || "",
        preview: msgs.find((m) => m.role === "user")?.text || "-",
        aiCount: msgs.filter((m) => m.role === "assistant" && m.source !== "faq").length,
      }))
      .sort((a, b) => b.last.localeCompare(a.last));
  }, [data]);

  if (!data) return <div className="text-slate-400">Memuat riwayat chat…</div>;
  const s = data.stats;

  return (
    <div>
      <AdminHeader title="Riwayat Chat — Tanya Bawaslu" description="Pantau percakapan chatbot dan pemakaian AI (Gemini) oleh pengunjung." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={CalendarDays} label="Pertanyaan Hari Ini" value={s.today} color="text-brandred bg-red-50" testid="chat-today" />
        <StatCard icon={MessagesSquare} label="Total Pertanyaan" value={s.total} testid="chat-total" />
        <StatCard icon={Sparkles} label="Dijawab AI" value={s.ai} color="text-purple-600 bg-purple-50" testid="chat-ai" />
        <StatCard icon={BookOpen} label="Dijawab FAQ" value={s.faq} color="text-emerald-600 bg-emerald-50" testid="chat-faq" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="chat-per-day-chart">
        <h3 className="font-bold text-navy mb-4">Pertanyaan Chatbot per Hari (14 hari terakhir)</h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={s.per_day}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="pertanyaan" fill="#DC2626" radius={[4, 4, 0, 0]} name="Pertanyaan" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="chat-sessions">
        <h3 className="font-bold text-navy mb-1">Percakapan per Sesi</h3>
        <p className="text-xs text-slate-400 mb-4">{sessions.length} sesi terekam</p>
        {sessions.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Belum ada percakapan.</p>}
        <Accordion type="single" collapsible>
          {sessions.map((sess) => (
            <AccordionItem key={sess.sid} value={sess.sid} data-testid={`chat-session-${sess.sid}`}>
              <AccordionTrigger className="hover:no-underline">
                <div className="flex items-center gap-3 text-left min-w-0">
                  <span className="h-8 w-8 rounded-full bg-navy text-white flex items-center justify-center shrink-0"><User className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{sess.preview}</div>
                    <div className="text-[11px] text-slate-400">
                      {new Date(sess.last).toLocaleString("id-ID")} · {sess.msgs.length} pesan
                      {sess.aiCount > 0 && <span className="ml-2 text-brandred font-semibold">{sess.aiCount} jawaban AI</span>}
                    </div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="bg-surface rounded-xl p-4 space-y-3 max-h-96 overflow-y-auto">
                  {sess.msgs.map((m) => (
                    <div key={m.id} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-brandred text-white" : "bg-navy text-white"}`}>
                        {m.role === "user" ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                      </div>
                      <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-brandred/10 text-slate-700" : "bg-white border border-slate-200 text-slate-700"}`}>
                        {m.role === "assistant" && (
                          <span className={`inline-block text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 mb-1 ${m.source === "faq" ? "bg-emerald-100 text-emerald-700" : "bg-purple-100 text-purple-700"}`}>
                            {m.source === "faq" ? "FAQ" : "AI"}
                          </span>
                        )}
                        <div className="whitespace-pre-wrap">{m.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
